import { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel } from "@/components/Reveal";
import HostPortrait from "@/components/HostPortrait";
import BookingWidget from "@/components/BookingWidget";
import SectionBg from "@/components/SectionBg";
import { AccordionItem } from "@/sections/Sections2";
import { scrollToId } from "@/lib/attribution";
import { HOST, COMPLIANCE, ASSETS } from "@/data/content";
import { REPLAY_BIO, OBJECTIONS, REPLAY_FAQS, BOOKING_TRUST } from "@/data/replayContent";

export const ReplayHost = () => (
  <section id="host" className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="replay-host-section">
    <SectionBg src={ASSETS.bg.veteranFamily} opacity={0.11} position="center 40%" />
    <div className="relative z-10">
      <SectionLabel num="004" text="YOUR ADVOCATE" />
      <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <Reveal className="max-w-md w-full mx-auto lg:mx-0"><HostPortrait /></Reveal>
        <div>
          <Reveal><h2 className="section-h">A VETERAN FIRST. A LOAN OFFICER SECOND. YOUR ADVOCATE THROUGH BOTH.</h2></Reveal>
          <Reveal delay={0.08}><div className="mt-6 h-px w-24 bg-[#D9622B]" /></Reveal>
          <div className="mt-8 space-y-5">
            {REPLAY_BIO.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.06}><p className="text-[15px] text-white/60 leading-[1.8]">{p}</p></Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[["COMPANY", HOST.company], ["NMLS#", HOST.nmls], ["CO. NMLS#", HOST.companyNmls], ["LICENSED IN", HOST.licensedIn]].map(([k, v]) => (
                <div key={k} className="bg-[#080808] p-5">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/30">{k}</p>
                  <p className="mt-2 text-sm font-bold text-[#F0ECE4]">{v}</p>
                </div>
              ))}
            </div>
            <button data-testid="replay-host-cta" onClick={() => scrollToId("book")} className="mt-8 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
              ✓ BOOK MY FREE VA BENEFIT REVIEW
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export const Objections = () => (
  <section className="px-6 sm:px-12 lg:px-16 py-28" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="objections-section">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
      <div>
        <SectionLabel num="005" text="LET'S BE HONEST" />
        <Reveal><h2 className="mt-6 section-h">{OBJECTIONS.heading}</h2></Reveal>
        <div className="mt-8 space-y-5">
          {OBJECTIONS.paras.map((p, i) => (
            <Reveal key={i} delay={0.08 + i * 0.06}><p className="text-[15px] text-white/60 leading-[1.8]">{p}</p></Reveal>
          ))}
        </div>
      </div>
      <div className="lg:pt-16">
        <Reveal>
          <p className="font-mono text-[12px] tracking-[0.25em] text-[#D9622B]">HERE'S WHAT HAPPENS WHEN YOU BOOK:</p>
          <ul className="mt-8 space-y-6">
            {OBJECTIONS.bullets.map((t, i) => (
              <li key={t} data-testid={`booking-bullet-${i + 1}`} className="flex gap-4 text-[15px] text-[#F0ECE4] leading-relaxed">
                <span className="text-[#D9622B] font-bold shrink-0">→</span>{t}
              </li>
            ))}
          </ul>
          <button data-testid="objections-cta" onClick={() => scrollToId("book")} className="mt-10 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
            ✓ BOOK MY FREE VA BENEFIT REVIEW
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const ReplayFAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="replay-faq-section">
      <SectionBg src={ASSETS.bg.luxHomeEstate} opacity={0.10} position="center 20%" />
      <div className="relative z-10">
        <SectionLabel num="006" text="BEFORE YOU BOOK" />
        <Reveal><h2 className="mt-6 section-h">COMMON QUESTIONS</h2></Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 border-t border-white/10 max-w-4xl">
            {REPLAY_FAQS.map((f, i) => (
              <AccordionItem key={f.q} num={String(i + 1).padStart(2, "0")} title={f.q} body={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} testId={`replay-faq-${i + 1}`} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const FinalBooking = () => (
  <section id="book" className="relative px-6 sm:px-12 lg:px-16 py-28 overflow-hidden grid-bg" data-testid="final-booking-section">
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(217,98,43,0.14), transparent 65%)" }} />
    <div className="relative grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionLabel num="007" text="FINAL STEP" />
        <Reveal>
          <h2 className="mt-6 final-h">GET A CLEAR ANSWER. <span className="text-[#D9622B]">NOT A GUESS.</span></h2>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="mt-10 space-y-4">
            {BOOKING_TRUST.map((t) => (
              <li key={t} className="flex gap-3 text-[15px] text-white/65">
                <span className="text-[#D9622B] font-bold">✓</span>{t}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="w-full lg:justify-self-end lg:max-w-xl">
        <BookingWidget />
      </Reveal>
    </div>
  </section>
);

export const ReplayFooter = () => (
  <footer className="relative border-t border-white/10 px-6 sm:px-12 lg:px-16 pt-20 pb-10 overflow-hidden" data-testid="replay-footer-section">
    <div className="footer-wordmark" aria-hidden="true">BOOTS-FIRST REVIEW</div>
    <div className="relative grid lg:grid-cols-3 gap-10 text-[13px] text-white/45">
      <div className="space-y-1.5">
        <span className="inline-flex items-center bg-white rounded px-3 py-2 mb-2">
          <img src={ASSETS.logo} alt="Loan Factory" className="h-6 w-auto" />
        </span>
        <p className="font-bold text-white/70">{HOST.name}</p>
        <p>{HOST.company}</p>
        <p>NMLS# {HOST.nmls} · Broker NMLS# {HOST.companyNmls}</p>
        <p>Licensed in Georgia, Florida, North Carolina & Tennessee</p>
      </div>
      <div className="space-y-1.5">
        <p className="font-mono text-[10px] tracking-[0.25em] text-white/30">CONTACT</p>
        <p>Phone: <a href={HOST.phoneHref} className="hover:text-white/70 transition-colors">{HOST.phone}</a></p>
        <p>Email: <a href={`mailto:${HOST.email}`} className="hover:text-white/70 transition-colors">{HOST.email}</a></p>
        <p>Web: {HOST.website}</p>
      </div>
      <div className="space-y-1.5">
        <p className="font-mono text-[10px] tracking-[0.25em] text-white/30">LEGAL</p>
        <p><Link to="/privacy" className="hover:text-white/70 transition-colors">Privacy Policy</Link></p>
        <p><a href="https://epiphanielending.com/opt_out" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Opt-Out</a></p>
        <p><Link to="/terms" className="hover:text-white/70 transition-colors">Terms</Link></p>
      </div>
    </div>
    <div className="relative mt-12 pt-8 border-t border-white/[0.06] flex gap-4 items-start">
      <span className="text-2xl text-white/40" title="Equal Housing Lender">⌂</span>
      <p className="text-[11px] text-white/30 leading-relaxed max-w-3xl">{COMPLIANCE}</p>
    </div>
    <p className="relative mt-8 font-mono text-[10px] text-white/[0.15]">© 2026 Epiphanie Lending · BOOTSFIRST.REPLAY v1.0</p>
  </footer>
);

export const ReplaySticky = () => (
  <div className="fixed bottom-0 left-0 right-0 z-40 p-3 md:hidden nav-glass border-t border-white/10" data-testid="replay-sticky-cta">
    <button onClick={() => scrollToId("book")} className="w-full py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
      ✓ BOOK MY FREE VA BENEFIT REVIEW
    </button>
  </div>
);
